import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getProduct, getProducts, getReviews, addReview } from '../firebase/database';
import { useCartStore, useWishlistStore, useAuthStore, useUIStore } from '../store';
import { formatPrice } from '../utils/helpers';
import ProductCard from '../components/shop/ProductCard';
import SEO from '../components/common/SEO';
import StarRating from '../components/common/StarRating';
import { Spinner } from '../components/common/Loader';
import { trackViewContent, trackAddToCart } from '../utils/pixel';
import ContactCTA from '../components/common/ContactCTA';
import toast from 'react-hot-toast';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [reviewForm, setReviewForm] = useState({ rating:5, comment:'', name:'' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);

  const { addItem } = useCartStore();
  const { toggle, isWishlisted } = useWishlistStore();
  const { user } = useAuthStore();
  const { setCartOpen } = useUIStore();

  const [loadError, setLoadError] = useState(null);

  useEffect(() => { if (id) loadProduct(); }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const prod = await getProduct(id);
      setProduct(prod);
      if (!prod) {
        setLoadError(`No product found with id "${id}"`);
        return;
      }
      trackViewContent(prod);
      // Set default variation selections
      const defaults = {};
      (prod.variations||[]).forEach(v => { if (v.values?.length>0) defaults[v.name] = v.values[0].label; });
      setSelectedVariations(defaults);
      const [rels, revs] = await Promise.all([getProducts({ limitCount:8 }), getReviews(id)]);
      setRelated(rels.filter(p=>p.id!==id).slice(0,4));
      setReviews(revs);
    } catch(err) {
      console.error('Product load error:', err);
      setLoadError(err.message || 'Failed to load product');
    }
    finally { setLoading(false); }
  };

  // Get selected variation value details
  const getSelectedVariationValue = () => {
    if (!product?.variations?.length) return null;
    // Find first variation and its selected value
    const firstVar = product.variations[0];
    if (!firstVar) return null;
    const selectedLabel = selectedVariations[firstVar.name];
    return firstVar.values?.find(v => v.label === selectedLabel) || null;
  };

  const selectedVarValue = getSelectedVariationValue();

  // Regular (non-sale) reference price: the variation's own price if it has one,
  // otherwise the product's base price.
  const regularPrice = selectedVarValue?.price ?? product?.price ?? 0;

  // Sale price: prefer the variation's own salePrice when this variation has one,
  // otherwise fall back to the product-level salePrice — but only as a fallback
  // when the variation itself didn't set its own price (so we're not mixing a
  // specific variation's price with a different variation's discount).
  const effectiveSalePrice = selectedVarValue?.salePrice ?? (selectedVarValue?.price ? null : product?.salePrice);

  const hasSale = effectiveSalePrice != null && effectiveSalePrice < regularPrice;
  const displayPrice = hasSale ? effectiveSalePrice : regularPrice;
  const displayImages = selectedVarValue?.images?.length > 0 ? selectedVarValue.images : (product?.images || []);
  const displayStock = selectedVarValue?.stock ?? product?.stock ?? 0;

  const getVariationLabel = () => {
    const parts = Object.entries(selectedVariations).map(([k,v]) => `${k}: ${v}`);
    return parts.join(', ');
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (displayStock <= 0) { toast.error('Out of stock'); return; }
    const cartItem = {
      ...product,
      images: displayImages,
      price: displayPrice,
      salePrice: null,
      selectedVariation: Object.keys(selectedVariations).length > 0 ? getVariationLabel() : null,
    };
    addItem(cartItem, quantity, getVariationLabel()||null);
    trackAddToCart(cartItem, quantity);
    setCartOpen(true);
    toast.success('Added to cart!');
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.comment.trim()) { toast.error('Please write a review'); return; }
    setSubmittingReview(true);
    try {
      await addReview({ productId:id, userId:user?.uid||'guest', name:user?.displayName||reviewForm.name||'Anonymous', rating:reviewForm.rating, comment:reviewForm.comment });
      setReviewForm({ rating:5, comment:'', name:'' });
      const revs = await getReviews(id);
      setReviews(revs);
      toast.success('Review submitted!');
    } catch { toast.error('Failed to submit'); }
    finally { setSubmittingReview(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner size="lg"/></div>;
  if (!product) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <h2 className="font-display text-3xl font-bold text-gray-800 mb-2">Product Not Found</h2>
        {loadError && (
          <p className="text-xs text-gray-400 mb-2 bg-gray-50 p-3 rounded-lg break-all">{loadError}</p>
        )}
        <p className="text-gray-500 text-sm mb-4">The product may have been removed or the link is incorrect. Try refreshing the page.</p>
        <div className="flex gap-3 justify-center">
          <button onClick={loadProduct} className="btn-outline">Retry</button>
          <Link to="/shop" className="btn-primary inline-block">Back to Shop</Link>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SEO title={product.metaTitle||product.title} description={product.metaDescription||product.description?.substring(0,160)} image={displayImages[0]}/>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#1a3a6e]">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#1a3a6e]">Shop</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium truncate max-w-xs">{product.title}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          {/* Images */}
          <div className="space-y-4">
            <motion.div className="relative overflow-hidden bg-gray-50 aspect-square cursor-zoom-in" onClick={()=>setZoomImg(displayImages[selectedImage])}>
              <AnimatePresence mode="wait">
                <motion.img key={`${selectedImage}_${JSON.stringify(selectedVariations)}`}
                  initial={{ opacity:0, scale:1.04 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0 }} transition={{ duration:0.3 }}
                  src={displayImages[selectedImage]||'https://via.placeholder.com/600?text=Product'} alt={product.title}
                  className="w-full h-full object-contain p-6"
                  onError={e=>e.target.src='https://via.placeholder.com/600x600?text=SitSmart'}/>
              </AnimatePresence>
              {hasSale&&<div className="absolute top-4 left-4 badge-sale">Sale</div>}
              {displayStock<=0&&<div className="absolute inset-0 bg-black/40 flex items-center justify-center"><span className="bg-white text-gray-900 font-bold px-6 py-3 text-lg">OUT OF STOCK</span></div>}
            </motion.div>
            {displayImages.length>1&&(
              <div className="flex gap-3 flex-wrap">
                {displayImages.map((img,i)=>(
                  <button key={i} onClick={()=>setSelectedImage(i)}
                    className={`w-20 h-20 overflow-hidden border-2 transition-all flex-shrink-0 ${selectedImage===i?'border-[#1a3a6e]':'border-gray-200 hover:border-gray-400'}`}>
                    <img src={img} alt={`View ${i+1}`} className="w-full h-full object-cover" onError={e=>e.target.src='https://via.placeholder.com/80'}/>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-gray-900 mt-2 mb-4 leading-tight">{product.title}</h1>
            <div className="flex items-center gap-3 mb-4">
              <StarRating rating={product.rating||0} size="md"/>
              <span className="text-sm text-gray-500">({reviews.length} reviews)</span>
              {displayStock>0 ? (
                <span className="text-green-600 text-sm font-medium flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  In Stock {displayStock<=10&&`(${displayStock} left)`}
                </span>
              ) : (
                <span className="text-red-500 text-sm font-medium flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Out of Stock
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-3xl font-black text-[#1a3a6e]">{formatPrice(displayPrice)}</span>
              {hasSale&&<><span className="text-lg text-gray-400 line-through">{formatPrice(regularPrice)}</span>
                <span className="badge-sale">Save {Math.round(((regularPrice-effectiveSalePrice)/regularPrice)*100)}%</span></>}
            </div>
            {product.shortDescription&&<p className="text-gray-600 text-sm leading-relaxed mb-6">{product.shortDescription}</p>}

            {/* Variations */}
            {(product.variations||[]).map((variation, vi) => (
              <div key={vi} className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-bold text-gray-900">{variation.name}:</span>
                  <span className="text-sm text-gray-500">{selectedVariations[variation.name]||'Select'}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(variation.values||[]).map((val, i) => {
                    const isSelected = selectedVariations[variation.name] === val.label;
                    const outOfStock = val.stock !== null && val.stock !== undefined && val.stock <= 0;
                    return (
                      <button key={i}
                        onClick={() => { if (!outOfStock) { setSelectedVariations(prev=>({...prev,[variation.name]:val.label})); setSelectedImage(0); } }}
                        disabled={outOfStock}
                        className={`px-4 py-2 text-sm font-semibold border-2 transition-all duration-200 relative ${
                          isSelected ? 'border-[#1a3a6e] bg-[#1a3a6e] text-white' :
                          outOfStock ? 'border-gray-200 text-gray-300 cursor-not-allowed line-through' :
                          'border-gray-200 text-gray-700 hover:border-[#1a3a6e] hover:text-[#1a3a6e]'
                        }`}>
                        {val.label}
                        {(() => {
                          const valRegular = val.price ?? product.price;
                          const valSale = val.salePrice;
                          const valHasSale = valSale != null && valSale < valRegular;
                          if (valHasSale) {
                            return (
                              <span className="block text-[10px] font-normal mt-0.5">
                                <span className="line-through opacity-60 mr-1">{formatPrice(valRegular)}</span>
                                <span className="font-bold">{formatPrice(valSale)}</span>
                              </span>
                            );
                          }
                          if (val.price && val.price !== product.price) {
                            return <span className="block text-[10px] font-normal mt-0.5 opacity-80">{formatPrice(val.price)}</span>;
                          }
                          return null;
                        })()}
                        {outOfStock && <span className="block text-[10px]">Out of stock</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-6">
              <span className="text-sm font-bold text-gray-900">Quantity:</span>
              <div className="flex items-center border border-gray-200">
                <button onClick={()=>setQuantity(Math.max(1,quantity-1))} className="px-3 py-2 hover:bg-gray-100 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4"/></svg>
                </button>
                <span className="px-5 py-2 font-bold text-gray-900 border-x border-gray-200 min-w-[3rem] text-center">{quantity}</span>
                <button onClick={()=>setQuantity(Math.min(displayStock,quantity+1))} disabled={quantity>=displayStock} className="px-3 py-2 hover:bg-gray-100 transition-colors disabled:opacity-40">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>
                </button>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mb-8">
              <motion.button whileTap={{ scale:0.97 }} onClick={handleAddToCart} disabled={displayStock<=0}
                className={`flex-1 flex items-center justify-center gap-2 py-4 font-bold text-sm tracking-wide transition-all duration-300 ${displayStock<=0?'bg-gray-200 text-gray-500 cursor-not-allowed':'bg-[#1a3a6e] text-white hover:bg-[#e63946]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/></svg>
                {displayStock<=0?'Out of Stock':'Add to Cart'}
              </motion.button>
              <motion.button whileTap={{ scale:0.97 }}
                onClick={()=>{ toggle(product); toast.success(isWishlisted(product.id)?'Removed from wishlist':'Added to wishlist!'); }}
                className={`px-4 py-4 border-2 transition-all duration-300 ${isWishlisted(product.id)?'border-[#e63946] bg-[#e63946] text-white':'border-gray-300 text-gray-600 hover:border-[#e63946] hover:text-[#e63946]'}`}>
                <svg className="w-5 h-5" fill={isWishlisted(product.id)?'currentColor':'none'} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
              </motion.button>
            </div>

            {/* Perks */}
            <div className="border border-gray-100 p-4 space-y-3">
              {[
                { icon:'truck', text:'Free delivery on orders over PKR 5,000' },
                { icon:'shield', text:'1-year manufacturer warranty' },
                { icon:'refresh', text:'Check parcel before paying rider — no claims after' },
              ].map(({ icon, text }) => (
                <div key={text} className="flex items-center gap-3 text-sm text-gray-600">
                  {icon==='truck'&&<svg className="w-4 h-4 text-[#1a3a6e] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/></svg>}
                  {icon==='shield'&&<svg className="w-4 h-4 text-[#1a3a6e] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>}
                  {icon==='refresh'&&<svg className="w-4 h-4 text-[#1a3a6e] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>}
                  {text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-20">
          <div className="flex border-b border-gray-200 mb-8">
            {['description','reviews','shipping'].map(tab=>(
              <button key={tab} onClick={()=>setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-semibold capitalize transition-all border-b-2 -mb-px ${activeTab===tab?'border-[#1a3a6e] text-[#1a3a6e]':'border-transparent text-gray-500 hover:text-gray-900'}`}>
                {tab}{tab==='reviews'&&` (${reviews.length})`}
              </button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }} transition={{ duration:0.3 }}>
              {activeTab==='description'&&(
                <div className="max-w-3xl">
                  <p className="text-gray-700 leading-relaxed">{product.description||'No description available.'}</p>
                  {product.features?.length>0&&(
                    <div className="mt-6"><h3 className="font-bold text-gray-900 mb-3">Key Features:</h3>
                      <ul className="space-y-2">
                        {product.features.map((f,i)=>(
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <div className="w-1.5 h-1.5 bg-[#e63946] rounded-full mt-2 flex-shrink-0"/>{f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {activeTab==='reviews'&&(
                <div className="max-w-3xl">
                  <div className="flex items-center gap-6 p-6 bg-gray-50 mb-8">
                    <div className="text-center">
                      <div className="text-5xl font-black text-[#1a3a6e]">{(product.rating||0).toFixed(1)}</div>
                      <StarRating rating={product.rating||0} size="md"/>
                      <p className="text-sm text-gray-500 mt-1">{reviews.length} reviews</p>
                    </div>
                  </div>
                  <div className="space-y-6 mb-10">
                    {reviews.length===0?<p className="text-gray-500 text-center py-8">No reviews yet. Be the first!</p>:
                      reviews.map(review=>(
                        <div key={review.id} className="border-b border-gray-100 pb-6">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-9 h-9 bg-[#1a3a6e] text-white rounded-full flex items-center justify-center font-bold text-sm">{review.name?.[0]?.toUpperCase()}</div>
                            <div><p className="font-semibold text-sm text-gray-900">{review.name}</p><StarRating rating={review.rating} size="sm"/></div>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed">{review.comment}</p>
                        </div>
                      ))}
                  </div>
                  <div className="bg-gray-50 p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Write a Review</h3>
                    <form onSubmit={handleSubmitReview} className="space-y-4">
                      <div><label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
                        <StarRating rating={reviewForm.rating} size="lg" interactive onChange={r=>setReviewForm(f=>({...f,rating:r}))}/></div>
                      {!user&&<div><label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                        <input type="text" value={reviewForm.name} onChange={e=>setReviewForm(f=>({...f,name:e.target.value}))} className="input-field" placeholder="Your name"/></div>}
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Your Review</label>
                        <textarea rows={4} value={reviewForm.comment} onChange={e=>setReviewForm(f=>({...f,comment:e.target.value}))} className="input-field resize-none" placeholder="Share your experience..."/></div>
                      <button type="submit" disabled={submittingReview} className="btn-primary flex items-center gap-2 text-sm">
                        {submittingReview&&<Spinner size="sm" color="white"/>}Submit Review
                      </button>
                    </form>
                  </div>
                </div>
              )}
              {activeTab==='shipping'&&(
                <div className="max-w-2xl space-y-4 text-sm text-gray-700 leading-relaxed">
                  <p><strong>Standard Delivery:</strong> 5-7 business days — PKR 250</p>
                  <p><strong>Express Delivery:</strong> 2-3 business days — PKR 500</p>
                  <p><strong>Same Day (Karachi only):</strong> Order by 12 PM</p>
                  <p><strong>Free Shipping:</strong> On all orders over PKR 5,000</p>
                  <p>Orders processed within 1-2 business days. Tracking info sent via SMS/WhatsApp after shipment.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {related.length>0&&(
          <div>
            <h2 className="font-display text-3xl font-black text-gray-900 mb-6">You May Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {related.map((p,i)=><ProductCard key={p.id} product={p} index={i}/>)}
            </div>
          </div>
        )}
      </div>

      <ContactCTA />

      {/* Image Zoom */}
      <AnimatePresence>
        {zoomImg&&(
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={()=>setZoomImg(null)}>
            <motion.img initial={{ scale:0.9 }} animate={{ scale:1 }} src={zoomImg} alt="Zoom" className="max-w-full max-h-full object-contain"/>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
export default ProductDetailPage;
